import { useState } from "react";
import axios from "axios";
import { Box, Typography, Button, Collapse, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { numberWithCommas } from "../../../utils/utility";

//import constants
import BASE_URL from "../../../constants/baseUrls";

//import components
import { BaseButton } from "../../common/base-button/base-button";
import BusItemLayout from "./busItemLayout";

export default function BusItem({
    uid,
    labels = [],
    companyLogo,
    companyName,
    ticketDate,
    origin,
    destination,
    departureTime,
    price,
    capacity,
    onSelect,
    sourceCode,
    busCode,
}) {
    const { t } = useTranslation();

    const [showRules, setShowRules] = useState(false);
    const [busItem, setBusItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [hasLoadedRules, setHasLoadedRules] = useState(false);

    const getBusItem = async () => {
        if (hasLoadedRules || isLoading) return;

        const accessToken = localStorage.getItem("accessToken");

        try {
            setIsLoading(true);
            setIsError(false);

            const response = await axios.post(
                `${BASE_URL}/api/v2/reserve/bus/detail/`,
                {
                    uid,
                    source_code: sourceCode,
                    bus_code: busCode,
                },
                {
                    headers: {
                        Authorization: accessToken ? `Bearer ${accessToken}` : "",
                    },
                },
            );

            setBusItem(response?.data?.data ?? null);
            setHasLoadedRules(true);
        } catch (error) {
            console.error("Failed to fetch bus details:", error);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowRules = () => {
        setShowRules(prev => {
            const next = !prev;

            if (next && !hasLoadedRules) {
                getBusItem();
            }

            return next;
        });
    };

    const renderRefundRules = () => {
        if (isLoading) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                        {t("loadingRefundRules", "در حال دریافت قوانین...")}
                    </Typography>
                </Box>
            );
        }

        if (isError) {
            return (
                <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}>
                    {t("refundRulesError", "دریافت قوانین استرداد با خطا مواجه شد.")}
                </Typography>
            );
        }

        if (!busItem?.refundRules?.length) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}>
                    {t("noRefundRules", "در حال حاضر دیتایی وجود ندارد.")}
                </Typography>
            );
        }

        return (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2, pr: 2 }}>
                {busItem.refundRules.map((rule, index) => (
                    <Typography
                        component="li"
                        key={`${rule}-${index}`}
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}
                    >
                        {rule}
                    </Typography>
                ))}
            </Box>
        );
    };

    return (
        <BusItemLayout
            labels={labels}
            logoUrl={companyLogo}
            companyName={companyName}
            ticketDate={ticketDate}
            origin={origin}
            destination={destination}
            departureTime={departureTime}
            isCardClickable={({ isXs, isSm }) => (isXs || isSm) && capacity > 0}
            onCardClick={onSelect}
            renderExtraContent={({ isXs, isSm }) =>
                !isXs && !isSm ? (
                    <>
                        <Button
                            size="small"
                            onClick={handleShowRules}
                            aria-expanded={showRules}
                            endIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        transform: showRules ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s ease",
                                    }}
                                />
                            }
                            sx={{ minWidth: 0, px: 0.5, fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.8rem" } }}
                        >
                            {t("refundRules")}
                        </Button>
                        <Collapse in={showRules}>{renderRefundRules()}</Collapse>
                    </>
                ) : null
            }
            renderFooter={({ isXs, isSm }) => (
                <>
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: "0.78rem", sm: "0.9rem", md: "1rem" }, whiteSpace: "nowrap" }}
                    >
                        {numberWithCommas(price)} {t("currency")}
                    </Typography>

                    {capacity > 0 && !isXs && !isSm && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                "& button, & a": {
                                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.5, sm: 0.75 },
                                },
                            }}
                        >
                            <BaseButton text={t("ticketSelection")} color="primary" size="medium" radius="radius-10" handleClick={onSelect} />
                        </Box>
                    )}

                    <Typography
                        variant="caption"
                        color={capacity > 0 ? "text.secondary" : "error"}
                        sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" }, whiteSpace: "nowrap" }}
                    >
                        {capacity > 0 ? `${t("capacity")}: ${capacity}` : t("fullCapacity")}
                    </Typography>
                </>
            )}
        />
    );
}
